const DEFAULT_API_BASE_URL_MALL = "https://dubaimall.looc.ai/api/user";
const DEV_API_BASE_URL_MALL = "https://dev-mall.looc.ai/api/user";
const DEV_MALL_MOBILE_NUMBERS = new Set([
  "971585829040",
  "971585810790",
  "971555527077",
  "971585948563",
  "966562877136",
]);

const getMallApiBaseUrl = (phoneNumber) => {
  const normalizedPhone = String(phoneNumber || "").replace(/\D/g, "");
  const isMallSubdomain =
    typeof window !== "undefined" &&
    window.location.hostname.startsWith("mall.");
  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.origin.includes("localhost"));

  //return DEV_MALL_MOBILE_NUMBERS.has(normalizedPhone) || isLocalhost
  return isLocalhost || isMallSubdomain
    ? DEV_API_BASE_URL_MALL
    : DEFAULT_API_BASE_URL_MALL;
};

/**
 * Send OTP to user via WhatsApp or SMS
 * @param {string} phoneNumber - Full phone number with country code (e.g., "971585829040")
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const sendOTP = async (phoneNumber) => {
  try {
    const apiBaseUrl = getMallApiBaseUrl(phoneNumber);
    const response = await fetch(`${apiBaseUrl}/mauthpin`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ user: phoneNumber }),
    });

    const data = await response.json();

    if (data.status === 0) {
      return { success: true, data };
    } else {
      return { success: false, error: data.msg || "Failed to send OTP" };
    }
  } catch (error) {
    console.error("Send OTP error:", error);
    return { success: false, error: "Network error. Please try again." };
  }
};

/**
 * Login user with phone number and OTP
 * @param {string} phoneNumber - Full phone number with country code
 * @param {string} otp - One-time password
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const loginWithOTP = async (phoneNumber, otp) => {
  try {
    const apiBaseUrl = getMallApiBaseUrl(phoneNumber);
    const response = await fetch(`${apiBaseUrl}/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ user: phoneNumber, pass: otp }),
    });

    const data = await response.json();

    if (data.status === 0 && data.data && data.data.token) {
      return { success: true, data: data.data };
    } else {
      return { success: false, error: data.msg || "Login failed" };
    }
  } catch (error) {
    console.error("Login error:", error);
    return { success: false, error: "Network error. Please try again." };
  }
};

/**
 * Guest login - no authentication required
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const guestLogin = async () => {
  try {
    // Use default production API URL for guest login
    // If user previously logged in, check localStorage for phone to determine dev vs prod
    const storedPhone = localStorage.getItem("phoneNumber");
    
    // Before calling /guest, check that phoneNumber != "Guest"
    if (storedPhone === "Guest") {
      // Already logged in as guest, skip API call and allow dismissing login screen
      const storedToken = localStorage.getItem("authToken");
      const storedUserData = localStorage.getItem("userData");
      
      if (storedToken && storedUserData) {
        // Return success with existing guest session data
        const userData = JSON.parse(storedUserData);
        return { 
          success: true, 
          data: userData,
          anonymousId: userData.anonymousId || generateAnonymousId()
        };
      }
      // If no valid session, continue to create new guest session
    }
    
    const apiBaseUrl = getMallApiBaseUrl(storedPhone);

    // Generate random alphanumeric ID: 'A' + 10 alphanumeric characters
    const randomId = generateAnonymousId();

    const response = await fetch(`${apiBaseUrl}/guest`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ user: randomId, pass: randomId }),
    });

    const data = await response.json();

    if (data.status === 0 && data.data && data.data.token) {
      return { success: true, data: data.data, anonymousId: randomId };
    } else {
      return { success: false, error: data.msg || "Guest login failed" };
    }
  } catch (error) {
    console.error("Guest login error:", error);
    return { success: false, error: "Network error. Please try again." };
  }
};

/**
 * Generate a random anonymous user ID
 * Format: A + 10 alphanumeric characters
 * @returns {string}
 */
export const generateAnonymousId = () => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let randomId = "A";
  for (let i = 0; i < 10; i++) {
    randomId += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return randomId;
};
